import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

import type { ChatMessage } from '../types';
import { ActionsWrap, Bubble, BubbleRow, ToolCalls } from '../styles';

import { ActionCard } from './ActionCard';
import { MessageFeedback } from './MessageFeedback';
import { ToolCallIndicator } from './ToolCallIndicator';

interface Props {
  message: ChatMessage;
  onResolveAction?: (decision: 'yes' | 'no') => void | Promise<boolean>;
  isStreaming?: boolean;
  // False for every assistant message except the latest; see ActionCard's `readOnly`.
  isActionable?: boolean;
  // Present once a server session exists; enables the feedback widget on completed
  // assistant messages. `userQuery` is the paired user message this response answered.
  sessionId?: string;
  userQuery?: string;
}

export function MessageBubble({
  message,
  onResolveAction,
  isStreaming = false,
  isActionable = true,
  sessionId,
  userQuery,
}: Props) {
  const isUser = message.role === 'user';

  return (
    <BubbleRow $isUser={isUser}>
      <Bubble $isUser={isUser}>
        {message.toolCalls && message.toolCalls.length > 0 && (
          <ToolCalls>
            {message.toolCalls.map((tc, i) => (
              <ToolCallIndicator key={`${tc.name}-${i}`} toolCall={tc} />
            ))}
          </ToolCalls>
        )}

        {message.content &&
          (isUser ? (
            message.content
          ) : (
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{message.content}</ReactMarkdown>
          ))}

        {message.actions && message.actions.length > 0 && !isStreaming && (
          <ActionsWrap>
            <ActionCard
              actions={message.actions}
              onResolve={onResolveAction}
              readOnly={!isActionable}
            />
          </ActionsWrap>
        )}

        {!isUser && message.content && !isStreaming && sessionId && (
          <MessageFeedback
            conversationId={message.id}
            llmResponse={message.content}
            sessionId={sessionId}
            userQuery={userQuery ?? ''}
          />
        )}
      </Bubble>
    </BubbleRow>
  );
}
